'use client';

import React, {useState, useEffect, useRef} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay, Navigation, Pagination} from 'swiper/modules';
import {Swiper as SwiperType} from 'swiper';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface Singer {
    id: number;
    name: string;
    imageUrl: string;
    category: string;
}

interface SingerSwiperProps {
    singers: Singer[];
    renderCard: (singer: Singer, isMobile: boolean) => React.ReactNode;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    loop?: boolean;
    isSectionInViewPort?: boolean;
}

const breakpoints = {
    320: {
        slidesPerView: 1,
        spaceBetween: 20,
    },
    375: {
        slidesPerView: 1.5,
        spaceBetween: 20,
    },
    480: {
        slidesPerView: 1.5,
        spaceBetween: 20,
    },
    640: {
        slidesPerView: 3,
        spaceBetween: 20,
    },
};

const MobileSingerSwiper: React.FC<SingerSwiperProps> = ({
    singers,
    renderCard,
    autoPlay = true,
    autoPlayInterval = 1000,
    loop = true,
    isSectionInViewPort,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const swiperRef = useRef<SwiperType>(null);

    useEffect(() => {
        if (isSectionInViewPort) {
            swiperRef?.current?.autoplay?.start();
        } else {
            swiperRef?.current?.autoplay?.pause();
        }
    }, [isSectionInViewPort]);

    const handleBeforeInit = (swiper: SwiperType) => {
        swiperRef.current = swiper;
        swiperRef?.current?.autoplay?.stop();
    };

    // Tính toán slide bắt đầu ở giữa
    const initialSlide = Math.floor(singers.length / 2);

    // Mobile component - sử dụng Swiper với touch support
    return (
        <div className="relative w-full">
            <Swiper
                modules={[Autoplay]}
                onBeforeInit={handleBeforeInit}
                onInit={handleBeforeInit}
                breakpoints={breakpoints}
                loop={loop}
                autoplay={
                    autoPlay
                        ? {
                              delay: autoPlayInterval,
                              disableOnInteraction: false,
                          }
                        : false
                }
                initialSlide={initialSlide}
                slidesPerView={1.5}
                centeredSlides={true}
                spaceBetween={20}
                grabCursor={true}
                allowTouchMove={true}
                onSlideChange={swiper => setActiveIndex(swiper.realIndex)}
                className="singer-swiper-mobile">
                {singers.map((singer, index) => (
                    <SwiperSlide key={singer.id} className="singer-slide-mobile">
                        {renderCard(singer, true)}
                    </SwiperSlide>
                ))}
            </Swiper>

            <style>{`
        .singer-swiper-mobile {
        width: 100%;
        padding: 0 40px;
        }
        
        .singer-slide-mobile {
        display: flex;
        justify-content: center;
        align-items: center;
        transition: all 0.3s ease;
        }
        
        .singer-swiper-mobile .swiper-slide {
        opacity: 0.6;
        transform: scale(0.75);
        transition: all 0.3s ease;
        }
        
        .singer-swiper-mobile .swiper-slide-active {
        opacity: 1;
        transform: scale(1);
        }
        
        .singer-swiper-mobile .swiper-slide-prev,
        .singer-swiper-mobile .swiper-slide-next {
        opacity: 0.6;
        transform: scale(0.75);
        }
    `}</style>
        </div>
    );
};

export default MobileSingerSwiper;
